import './ReplacingSummary.scss';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { MdAssignmentInd, MdExpandMore } from 'react-icons/md';
import { UserService } from '@services/UserService';
import { IReplacingPerson } from '@models/IReplacingPerson';
import { useTypedSelector } from '@hooks/useTypedSelector';
import UserAvatar from '@molecules/UserAvatar/UserAvatar';
import UserName from '@molecules/UserName/UserName';
import { useAvatarMeta } from '@molecules/UserAvatar/useAvatarMeta';
import { safeFormatDate } from '@/utils/date';
import { plural } from '@srm/ui/lib/pluralize';
import { POPOVER_Z_INDEX } from '@srm/ui/lib/zIndex';

/** Сколько аватаров показываем в свёрнутой стопке до «+N». */
const STACK_LIMIT = 3;

/** Тег строки: «Постоянно» / «до DD.MM.YYYY» (ВРИО, если известна дата) / «Временно». */
function tagText(person: IReplacingPerson): string {
    if (person.kind !== 'temporary') return 'Постоянно';
    const end = safeFormatDate(person.endDate, 'DD.MM.YYYY');
    return end ? `до ${end}` : 'Временно';
}

/** Аватар одного замещаемого; мета фото батчится через useAvatarMeta (без 404-шторма). */
const PersonAvatar: React.FC<{ person: IReplacingPerson; size: number; className?: string }> = ({
    person,
    size,
    className,
}) => {
    const meta = useAvatarMeta(person.key);
    return (
        <UserAvatar
            userKey={person.key}
            name={person.name}
            hasImage={meta?.hasImage ?? false}
            photoVersion={meta?.photoVersion ?? undefined}
            size={size}
            className={className}
        />
    );
};

/**
 * Блок «Вы замещаете» в меню пользователя: кого текущий сотрудник замещает (его назначил
 * кто-то другой). Read-only — повлиять на чужое назначение нельзя, поэтому без действий.
 * По умолчанию — компактная сводка со стопкой аватаров; клик разворачивает полный список
 * (аватар одного размера + ФИО как UserHoverCard + тег постоянно/временно). Данные грузятся
 * на маунте = при каждом открытии меню (Mantine Dialog размонтирует содержимое закрытым).
 */
const ReplacingSummary: React.FC = () => {
    const canManage = useTypedSelector((s) => s.auth.user?.canView?.setAssistantSimple ?? false);
    const [people, setPeople] = useState<IReplacingPerson[]>([]);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!canManage) return;
        let alive = true;
        UserService.getReplacing()
            .then((r) => {
                if (alive) setPeople(Array.isArray(r.data) ? r.data : []);
            })
            .catch(() => {
                if (alive) setPeople([]);
            });
        return () => {
            alive = false;
        };
    }, [canManage]);

    if (people.length === 0) return null;

    const count = people.length;
    const stack = people.slice(0, STACK_LIMIT);
    const rest = count - stack.length;

    return (
        <div className={clsx('rf-repl', expanded && 'rf-repl--open')}>
            <button
                type="button"
                className="rf-repl__bar"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
            >
                <span className="rf-repl__bar-ic" aria-hidden>
                    <MdAssignmentInd size={18} />
                </span>
                <span className="rf-repl__bar-text">
                    {/* «замещать» + одушевлённое → винительный = родительный: 1 коллегу, 2/5 коллег */}
                    Вы замещаете <b>{count}</b> {plural(count, ['коллегу', 'коллег', 'коллег'])}
                </span>
                {/* Стопка видна и в развёрнутом виде — иначе бар «прыгает» по высоте. */}
                <span className="rf-repl__stack" aria-hidden>
                    {stack.map((person) => (
                        <PersonAvatar key={person.key} person={person} size={24} className="rf-repl__stack-av" />
                    ))}
                    {rest > 0 && <span className="rf-repl__stack-more">+{rest}</span>}
                </span>
                <span className="rf-repl__chev" aria-hidden>
                    <MdExpandMore size={18} />
                </span>
            </button>

            {expanded && (
                <div className="rf-repl__list">
                    {people.map((person) => (
                        <div className="rf-repl__row" key={person.key}>
                            <PersonAvatar person={person} size={30} />
                            <span className="rf-repl__name">
                                <UserName
                                    userKey={person.key}
                                    name={person.name}
                                    position="left"
                                    hoverCardZIndex={POPOVER_Z_INDEX}
                                />
                            </span>
                            <span
                                className={clsx(
                                    'rf-repl__tag',
                                    person.kind === 'temporary' && 'rf-repl__tag--temp',
                                )}
                            >
                                <span className="rf-repl__tdot" aria-hidden />
                                {tagText(person)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReplacingSummary;
